import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { Account } from '../../services/types';
import { AccountLedgerDialog } from '../../components/financials/AccountLedgerDialog';
import { CompactTable } from '../../components/ui/CompactTable';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { includesNormalized } from '../../components/table-filters/filterPredicates';
import { useTableFilterState } from '../../hooks/useTableFilterState';

export function TrialBalance() {
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [ledgerAccount, setLedgerAccount] = useState<Pick<Account, 'id' | 'code' | 'name' | 'type'> | null>(null);
    const [ledgerOpen, setLedgerOpen] = useState(false);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        type: 'all'
    });

    useEffect(() => {
        let active = true;

        async function loadAccounts() {
            try {
                setLoading(true);
                const payload = await api.financials.listAccounts();
                if (active) {
                    setAccounts(payload);
                }
            } catch (error: any) {
                if (active) {
                    toast.error(error?.message || 'Failed to load trial balance accounts');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }

        void loadAccounts();

        return () => {
            active = false;
        };
    }, []);

    const filteredAccounts = accounts.filter((account) => {
        const matchesSearch = includesNormalized(
            [account.code, account.name, account.type, account.balance].join(' '),
            state.q
        );
        const matchesType = state.type === 'all' || account.type === state.type;
        return matchesSearch && matchesType;
    });

    const totalDebit = filteredAccounts.reduce((sum, acc) => sum + (['Asset', 'Expense'].includes(acc.type) ? acc.balance : 0), 0);
    const totalCredit = filteredAccounts.reduce((sum, acc) => sum + (['Liability', 'Equity', 'Revenue', 'Income'].includes(acc.type) ? acc.balance : 0), 0);

    const columns = [
        { header: 'Code', accessor: (a: Account) => a.code, className: 'font-medium w-1/6 min-w-[80px]' },
        {
            header: 'Account Name',
            accessor: (a: Account) => (
                <button
                    type="button"
                    className="truncate text-left font-medium text-blue-700 hover:underline"
                    onClick={() => {
                        setLedgerAccount({ id: a.id, code: a.code, name: a.name, type: a.type });
                        setLedgerOpen(true);
                    }}
                >
                    {a.name}
                </button>
            ),
            className: 'w-2/6 min-w-[220px]'
        },
        { header: 'Type', accessor: (a: Account) => a.type, className: 'text-muted-foreground w-1/6' },
        {
            header: 'Debit',
            accessor: (a: Account) => ['Asset', 'Expense'].includes(a.type) ? Number(a.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-',
            className: 'text-right w-1/6'
        },
        {
            header: 'Credit',
            accessor: (a: Account) => ['Liability', 'Equity', 'Revenue', 'Income'].includes(a.type) ? Number(a.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-',
            className: 'text-right w-1/6'
        }
    ];

    return (
        <Card>
            <CardHeader>
                <CardTitle>Trial Balance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <TableFilterBar
                    searchValue={state.q}
                    onSearchChange={(value) => setFilter('q', value)}
                    searchPlaceholder="Search by code, account name, type..."
                    controls={[
                        {
                            key: 'type',
                            label: 'Type',
                            type: 'select',
                            value: state.type,
                            onChange: (value) => setFilter('type', value),
                            options: [
                                { value: 'all', label: 'All Types' },
                                { value: 'Asset', label: 'Asset' },
                                { value: 'Liability', label: 'Liability' },
                                { value: 'Equity', label: 'Equity' },
                                { value: 'Revenue', label: 'Revenue' },
                                { value: 'Income', label: 'Income' },
                                { value: 'Expense', label: 'Expense' }
                            ]
                        }
            ]}
                    isDirty={isDirty}
                    onClear={clearAll}
                    rightSlot={<span>Click an account name to inspect the journal-level ledger breakdown.</span>}
                />
                <CompactTable data={filteredAccounts} columns={columns} keyField="id" loading={loading} />

                <div className="bg-muted rounded-md p-3 flex justify-end gap-8 text-sm font-medium">
                    <span>Total Debit: {totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    <span>Total Credit: {totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
            </CardContent>
            <AccountLedgerDialog open={ledgerOpen} onOpenChange={setLedgerOpen} account={ledgerAccount} />
        </Card>
    );
}
